import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";
import Plot from "react-plotly.js";
import { toast } from "react-toastify";

import { supportGetLoadTimingStats } from "src/apiService/modules/support";
import type { LoadTimingStats } from "src/interfaces/loadTimingStats.interface";
import {
  FEATURE_FLAGS,
  type FeatureFlag,
} from "src/interfaces/systemState.interface";
import SupportDateRangeFilter from "src/pages/support/SupportDateRangeFilter";
import {
  DayMs,
  rangeForSupportDatePreset,
  type SupportDateRangePresetId,
} from "src/pages/support/dateRangePresets";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken, supportIsAdmin } from "src/store/support/selector";

const AllFeatureFlags = "all";

const GroupColors: Record<string, string> = {
  all: "#1f77b4",
  with: "#1f77b4",
  without: "#ff7f0e",
};

const Stats = [
  { key: "avg", label: "Average", dash: "solid" },
  { key: "p95", label: "p95", dash: "dash" },
  { key: "p99", label: "p99", dash: "dot" },
] as const;

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
  },
  chartWrapper: {
    width: "100%",
  },
  chart: {
    width: "100%",
    minHeight: 460,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  control: {
    minWidth: 220,
  },
}));

function formatBucketLabel(
  bucket: { start: string },
  bucketMs: number
): string {
  const start = new Date(bucket.start);
  if (bucketMs <= DayMs) return format(start, "MMM d, HH:mm");
  return format(start, "MMM d");
}

function prettifyKey(key: string): string {
  const withSpaces = key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

function metricLabel(metric: string): string {
  if (metric === "total") return "Total (shell ready)";
  return prettifyKey(metric);
}

function buildTraces(stats: LoadTimingStats, metric: string) {
  const x = stats.buckets.map((bucket) =>
    formatBucketLabel(bucket, stats.bucketMs)
  );

  const traces: Plotly.Data[] = [];
  for (const group of stats.groups) {
    const summary = stats.series[metric]?.[group.key];
    if (!summary) continue;

    for (const stat of Stats) {
      traces.push({
        x,
        y: summary[stat.key],
        customdata: summary.count,
        name: `${group.label} · ${stat.label}`,
        type: "scatter",
        // Markers keep a single sample (or isolated buckets) visible; a
        // lines-only trace draws nothing when there is no adjacent point.
        mode: "lines+markers",
        connectgaps: false,
        line: {
          color: GroupColors[group.key] || "#7f7f7f",
          width: stat.key === "avg" ? 2.5 : 1.5,
          dash: stat.dash,
        },
        marker: {
          color: GroupColors[group.key] || "#7f7f7f",
          size: 6,
          symbol:
            stat.key === "avg"
              ? "circle"
              : stat.key === "p95"
              ? "square"
              : "diamond",
        },
        hovertemplate:
          "<b>%{fullData.name}</b><br />%{x}<br />%{y:.0f} ms (%{customdata} samples)<extra></extra>",
      } as Plotly.Data);
    }
  }

  return traces;
}

function LoadTimings() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);
  const isAdmin = useSelector(supportIsAdmin);

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<LoadTimingStats | undefined>();
  const [selectedMetric, setSelectedMetric] = useState("total");
  const [selectedFlag, setSelectedFlag] = useState<string>(AllFeatureFlags);
  const [activePreset, setActivePreset] =
    useState<SupportDateRangePresetId | null>("1d");
  const [from, setFrom] = useState(() => new Date(Date.now() - DayMs));
  const [to, setTo] = useState(() => new Date());

  const fetchStats = useCallback(
    async (fromDate: Date, toDate: Date, featureFlag: string) => {
      if (!token) return;

      setLoading(true);
      try {
        const { data } = await supportGetLoadTimingStats(
          token,
          fromDate.toISOString(),
          toDate.toISOString(),
          featureFlag === AllFeatureFlags ? undefined : featureFlag
        );
        setStats(data);
        setSelectedMetric((current) => {
          if (data.metrics.includes(current)) return current;
          if (data.metrics.includes("total")) return "total";
          return data.metrics[0] || "total";
        });
      } catch (err: any) {
        if (err?.code === "failed-precondition") {
          dispatch(setSupportLoginToken());
        }
        setStats(undefined);
        toast.error(err?.message || "Failed to load load timings");
      } finally {
        setLoading(false);
      }
    },
    [dispatch, token]
  );

  const applyPreset = useCallback(
    (presetId: SupportDateRangePresetId, fromDate: Date, toDate: Date) => {
      setActivePreset(presetId);
      setFrom(fromDate);
      setTo(toDate);
      void fetchStats(fromDate, toDate, selectedFlag);
    },
    [fetchStats, selectedFlag]
  );

  useEffect(() => {
    if (!isAdmin || !token) return;
    const range = rangeForSupportDatePreset("1d");
    applyPreset("1d", range.from, range.to);
    // Only fetch the initial range when the page becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, token]);

  const handleFlagChange = (featureFlag: string) => {
    setSelectedFlag(featureFlag);
    void fetchStats(from, to, featureFlag);
  };

  const metricOptions = stats?.metrics.length ? stats.metrics : ["total"];

  const plot = useMemo(() => {
    if (!stats?.buckets.length || !stats.metrics.length) return null;

    const traces = buildTraces(stats, selectedMetric);
    if (!traces.length) return null;

    const denseXLabels = stats.buckets.length > 12;

    return (
      <Box className={classes.chartWrapper}>
        <Plot
          className={classes.chart}
          useResizeHandler
          style={{ width: "100%", height: "100%" }}
          data={traces}
          revision={stats.buckets.length}
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: 460,
            width: undefined,
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: {
              color: theme.palette.text.primary,
            },
            margin: {
              l: 60,
              r: 16,
              t: 72,
              b: denseXLabels ? 96 : 64,
            },
            xaxis: {
              title: "Time",
              tickangle: denseXLabels ? -40 : 0,
              automargin: true,
              nticks: denseXLabels
                ? Math.min(12, stats.buckets.length)
                : undefined,
            },
            yaxis: {
              title: "Load time (ms)",
              rangemode: "tozero",
              automargin: true,
            },
            legend: {
              orientation: "h",
              yanchor: "bottom",
              y: 1.02,
              x: 0,
              xanchor: "left",
            },
            showlegend: true,
          }}
        />
      </Box>
    );
  }, [
    stats,
    selectedMetric,
    classes.chart,
    classes.chartWrapper,
    theme.palette.text.primary,
  ]);

  if (!isAdmin) {
    return (
      <Box m={2} component={Paper} className={classes.paper}>
        <Typography color="textSecondary">
          Admin access is required to view load timing analytics.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component={Paper} className={classes.paper}>
      <Typography variant="h6" gutterBottom>
        Data Load Timings
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        One snapshot per app load, written once the shell is usable. Choose a
        feature flag to compare users that had it enabled against users that did
        not. Percentiles use the samples in each bucket, so hover a point to see
        how many loads it is based on.
      </Typography>

      <SupportDateRangeFilter
        from={from}
        to={to}
        activePreset={activePreset}
        onPresetChange={applyPreset}
        onFromChange={setFrom}
        onToChange={setTo}
        onApply={() => {
          setActivePreset(null);
          void fetchStats(from, to, selectedFlag);
        }}
      />

      <Box className={classes.controls}>
        <FormControl
          variant="outlined"
          size="small"
          className={classes.control}
        >
          <InputLabel id="load-timing-metric-label">Timing</InputLabel>
          <Select
            labelId="load-timing-metric-label"
            label="Timing"
            value={selectedMetric}
            onChange={(event) =>
              setSelectedMetric(event.target.value as string)
            }
          >
            {metricOptions.map((metric) => (
              <MenuItem key={metric} value={metric}>
                {metricLabel(metric)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          size="small"
          className={classes.control}
        >
          <InputLabel id="load-timing-flag-label">Feature flag</InputLabel>
          <Select
            labelId="load-timing-flag-label"
            label="Feature flag"
            value={selectedFlag}
            onChange={(event) => handleFlagChange(event.target.value as string)}
          >
            <MenuItem value={AllFeatureFlags}>All users</MenuItem>
            {FEATURE_FLAGS.map((flag: FeatureFlag) => (
              <MenuItem key={flag} value={flag}>
                Compare {flag}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      ) : plot ? (
        plot
      ) : (
        <Typography color="textSecondary">
          No load-timing events in this range.
        </Typography>
      )}
    </Box>
  );
}

export default LoadTimings;
